import React, {useEffect, useState, useMemo} from 'react';
import {Button, Col, Form, InputNumber, Modal, Popconfirm, Row, Select, Table, Tag, message, Card, Statistic, Divider, Space, Alert, Empty, Spin} from "antd";
import {CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined, RocketOutlined} from "@ant-design/icons";
import Chart from 'react-apexcharts';
import api from "../../api";
import {useTranslations} from "../../lang";
import {DEFAULT_GUTTER} from "../../constants";

const PRIORITIES = ['high', 'medium', 'low'];

const MONTH_NAMES = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
const MONTH_FIRST_WEEKS = [1, 5, 9, 14, 18, 23, 27, 31, 36, 40, 45, 49];
const MONTH_LAST_WEEKS = [4, 8, 13, 17, 22, 26, 30, 35, 39, 44, 48, 52];

const weekOptions = Array.from({length: 52}, (_, i) => {
    const week = i + 1;
    let monthIndex = 0;
    for (let m = 0; m < 12; m++) {
        if (week >= MONTH_FIRST_WEEKS[m] && week <= MONTH_LAST_WEEKS[m]) {
            monthIndex = m;
            break;
        }
    }
    return {value: week, label: `${MONTH_NAMES[monthIndex]} - week ${week}`};
});

const PRIORITY_COLORS = {
    high: 'red',
    medium: 'orange',
    low: 'green',
};

function renderWarning(warning, translate) {
    if (typeof warning === 'string') return warning;
    if (warning && warning.key) {
        let text = translate(warning.key);
        if (warning.params) {
            Object.entries(warning.params).forEach(([k, v]) => {
                text = text.replace(`{${k}}`, String(v));
            });
        }
        return text;
    }
    return String(warning);
}

export default function AutoPlanner() {
    const {translate} = useTranslations();
    const currentYear = new Date().getFullYear();

    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingGoal, setEditingGoal] = useState(null);
    const [year, setYear] = useState(currentYear);
    const [crops, setCrops] = useState([]);
    const [cropsLoading, setCropsLoading] = useState(false);
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // Generate plan state
    const [planResult, setPlanResult] = useState(null);
    const [planLoading, setPlanLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [addingToPlan, setAddingToPlan] = useState(false);
    const [beds, setBeds] = useState([]);
    const [bedAssignments, setBedAssignments] = useState({});

    const loadGoals = () => {
        setLoading(true);
        api.getHarvestGoals(year).then((data) => {
            setGoals(data);
        }).catch(() => {
            message.error(translate('auto_planner.error.loading_goals'));
        }).finally(() => setLoading(false));
    };

    useEffect(() => {
        loadGoals();
    }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load beds from all gardens
    useEffect(() => {
        api.gardens().then((gardens) => {
            const bedPromises = gardens.map(g => api.beds(g.id).then(gardenBeds =>
                gardenBeds.map(b => ({...b, garden_name: g.name}))
            ));
            Promise.all(bedPromises).then((results) => {
                setBeds(results.flat());
            });
        });
    }, []);

    const loadCrops = (search = '') => {
        setCropsLoading(true);
        api.crops({search, sort: 'name_asc'}).then((data) => {
            setCrops(data);
        }).finally(() => setCropsLoading(false));
    };

    const openAddModal = () => {
        setEditingGoal(null);
        form.resetFields();
        form.setFieldsValue({
            priority: 'medium',
            year: year,
        });
        setModalOpen(true);
        loadCrops();
    };

    const openEditModal = (goal) => {
        setEditingGoal(goal);
        form.setFieldsValue({
            crop_id: goal.crop_id,
            amount_per_week: goal.amount_per_week,
            start_week: goal.start_week,
            end_week: goal.end_week,
            priority: goal.priority,
            year: goal.year,
        });
        setModalOpen(true);
        loadCrops();
    };

    const handleSubmit = (values) => {
        const data = {...values, year};
        setSubmitting(true);
        if (editingGoal) {
            api.updateHarvestGoal(editingGoal.id, data).then(() => {
                message.success(translate('auto_planner.success.goal_updated'));
                setModalOpen(false);
                loadGoals();
            }).catch(() => {
                message.error(translate('auto_planner.error.saving_goal'));
            }).finally(() => setSubmitting(false));
        } else {
            api.createHarvestGoal(data).then(() => {
                message.success(translate('auto_planner.success.goal_created'));
                setModalOpen(false);
                loadGoals();
            }).catch(() => {
                message.error(translate('auto_planner.error.saving_goal'));
            }).finally(() => setSubmitting(false));
        }
    };

    const handleDelete = (id) => {
        api.deleteHarvestGoal(id).then(() => {
            message.success(translate('auto_planner.success.goal_deleted'));
            loadGoals();
        }).catch(() => {
            message.error(translate('auto_planner.error.deleting_goal'));
        });
    };

    const handleGeneratePlan = () => {
        setPlanLoading(true);
        setPlanResult(null);
        setSelectedRowKeys([]);
        setBedAssignments({});
        api.generatePlan({year}).then((data) => {
            setPlanResult(data);
            // Select all rows by default
            if (data.proposed_plantings) {
                setSelectedRowKeys(data.proposed_plantings.map((_, i) => i));
            }
        }).catch(() => {
            message.error(translate('auto_planner.error.generating_plan'));
        }).finally(() => setPlanLoading(false));
    };

    const handleBedChange = (rowIndex, bedId) => {
        setBedAssignments(prev => ({...prev, [rowIndex]: bedId}));
    };

    const createPlantingsFromRows = (rowKeys) => {
        if (!planResult || rowKeys.length === 0) return;

        Modal.confirm({
            title: translate('auto_planner.confirm.add_plantings_title'),
            content: translate('auto_planner.confirm.add_plantings_message').replace('{count}', String(rowKeys.length)),
            okText: translate('Ja'),
            cancelText: translate('Nee'),
            onOk: () => {
                setAddingToPlan(true);
                const plantings = rowKeys.map((key) => {
                    const p = planResult.proposed_plantings[key];
                    const planting = {
                        crop_id: p.crop_id,
                        sow_at: p.sow_at,
                        prick_at: p.prick_at,
                        transplant_at: p.transplant_at,
                        harvest_at: p.harvest_at,
                        harvest_until: p.harvest_until,
                    };
                    if (bedAssignments[key]) {
                        planting.bed_id = bedAssignments[key];
                    }
                    return planting;
                });

                const promises = plantings.map(p => api.addPlanting(p));
                return Promise.all(promises).then(() => {
                    message.success(translate('auto_planner.success.plantings_added').replace('{count}', String(plantings.length)));
                    // Remove added rows from selection
                    setSelectedRowKeys(prev => prev.filter(k => !rowKeys.includes(k)));
                }).catch(() => {
                    message.error(translate('auto_planner.error.adding_plantings'));
                }).finally(() => setAddingToPlan(false));
            },
        });
    };

    const yearOptions = [];
    for (let y = currentYear - 1; y <= currentYear + 2; y++) {
        yearOptions.push({value: y, label: String(y)});
    }

    // Harvest Goals table columns
    const columns = [
        {
            title: translate('Gewas'),
            key: 'crop',
            render: (record) => record.crop ? record.crop.name : `Crop #${record.crop_id}`,
        },
        {
            title: translate('Hoeveelheid/week'),
            dataIndex: 'amount_per_week',
            key: 'amount_per_week',
            responsive: ['md'],
        },
        {
            title: translate('Eenheid'),
            dataIndex: 'unit',
            key: 'unit',
            responsive: ['md'],
            render: (unit) => translate(unit),
        },
        {
            title: translate('Startweek'),
            dataIndex: 'start_week',
            key: 'start_week',
            responsive: ['md'],
        },
        {
            title: translate('Eindweek'),
            dataIndex: 'end_week',
            key: 'end_week',
            responsive: ['md'],
        },
        {
            title: translate('Prioriteit'),
            dataIndex: 'priority',
            key: 'priority',
            render: (priority) => (
                <Tag color={PRIORITY_COLORS[priority]}>{translate(priority)}</Tag>
            ),
        },
        {
            title: translate('Acties'),
            key: 'actions',
            render: (record) => (
                <>
                    <Button
                        size="small"
                        icon={<EditOutlined/>}
                        onClick={() => openEditModal(record)}
                        className="mar-r-1"
                    />
                    <Popconfirm
                        title={translate('auto_planner.confirm.delete_goal')}
                        onConfirm={() => handleDelete(record.id)}
                        okText={translate('Ja')}
                        cancelText={translate('Nee')}
                    >
                        <Button size="small" danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </>
            ),
        },
        {
            title: translate('Oogstdoel'),
            key: 'mobile',
            responsive: ['xs'],
            render: (record) => (
                <Row gutter={[8, 4]}>
                    <Col span={24}>
                        <strong>{record.crop ? record.crop.name : `Crop #${record.crop_id}`}</strong>
                    </Col>
                    <Col span={24}>
                        {record.amount_per_week} {translate(record.unit)}/{translate('week')} — {translate('week')} {record.start_week}-{record.end_week}
                    </Col>
                    <Col span={24}>
                        <Tag color={PRIORITY_COLORS[record.priority]}>{translate(record.priority)}</Tag>
                        <Button size="small" icon={<EditOutlined/>} onClick={() => openEditModal(record)} className="mar-r-1"/>
                        <Popconfirm
                            title={translate('auto_planner.confirm.delete_goal')}
                            onConfirm={() => handleDelete(record.id)}
                            okText={translate('Ja')}
                            cancelText={translate('Nee')}
                        >
                            <Button size="small" danger icon={<DeleteOutlined/>}/>
                        </Popconfirm>
                    </Col>
                </Row>
            ),
        },
    ];

    // Hide individual columns on mobile since we have the combined mobile column
    const desktopColumns = columns.filter(c => c.key !== 'mobile').map(c => ({...c, responsive: c.responsive || ['md']}));
    const mobileColumns = columns.filter(c => c.key === 'mobile');
    const allColumns = [...desktopColumns, ...mobileColumns];

    // Proposed Sowings table columns
    const bedOptions = beds.map(b => ({
        value: b.id,
        label: b.garden_name ? `${b.garden_name} — ${b.name}` : b.name,
    }));

    const planColumns = [
        {
            title: translate('Gewas'),
            dataIndex: 'crop_name',
            key: 'crop_name',
            sorter: (a, b) => (a.crop_name || '').localeCompare(b.crop_name || ''),
        },
        {
            title: translate('Zaaidatum'),
            dataIndex: 'sow_at',
            key: 'sow_at',
            sorter: (a, b) => (a.sow_at || '').localeCompare(b.sow_at || ''),
        },
        {
            title: translate('Verspenen'),
            dataIndex: 'prick_at',
            key: 'prick_at',
            responsive: ['md'],
            render: (val) => val || '—',
        },
        {
            title: translate('Uitplanten'),
            dataIndex: 'transplant_at',
            key: 'transplant_at',
            responsive: ['md'],
            render: (val) => val || '—',
        },
        {
            title: translate('Oogst van'),
            dataIndex: 'harvest_at',
            key: 'harvest_at',
        },
        {
            title: translate('Oogst tot'),
            dataIndex: 'harvest_until',
            key: 'harvest_until',
            responsive: ['md'],
            render: (val) => val || '—',
        },
        {
            title: translate('Aantal'),
            dataIndex: 'desired_amount',
            key: 'desired_amount',
            responsive: ['md'],
        },
        {
            title: translate('Bed'),
            key: 'bed',
            width: 200,
            render: (_, __, index) => (
                <Select
                    allowClear
                    placeholder={translate('Kies bed')}
                    style={{width: '100%'}}
                    options={bedOptions}
                    value={bedAssignments[index] || undefined}
                    onChange={(val) => handleBedChange(index, val)}
                    size="small"
                />
            ),
        },
        {
            title: translate('Waarschuwingen'),
            dataIndex: 'warnings',
            key: 'warnings',
            render: (warnings) => (
                <>
                    {(warnings || []).map((w, i) => (
                        <Tag color="orange" key={i}>{renderWarning(w, translate)}</Tag>
                    ))}
                </>
            ),
        },
    ];

    // Build chart data from proposed plantings
    const chartData = useMemo(() => {
        if (!planResult || !planResult.proposed_plantings || planResult.proposed_plantings.length === 0) {
            return null;
        }

        const plantings = planResult.proposed_plantings;

        // Get unique crops
        const cropNames = [...new Set(plantings.map(p => p.crop_name))];

        // Find week range
        const getWeek = (dateStr) => {
            if (!dateStr) return null;
            const d = new Date(dateStr);
            const onejan = new Date(d.getFullYear(), 0, 1);
            return Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
        };

        let minWeek = 53;
        let maxWeek = 0;
        plantings.forEach(p => {
            const hw = getWeek(p.harvest_at);
            const hu = getWeek(p.harvest_until);
            if (hw && hw < minWeek) minWeek = hw;
            if (hw && hw > maxWeek) maxWeek = hw;
            if (hu && hu > maxWeek) maxWeek = hu;
            if (hu && hu < minWeek) minWeek = hu;
        });

        if (minWeek > maxWeek) return null;

        const weeks = [];
        for (let w = minWeek; w <= maxWeek; w++) {
            weeks.push(w);
        }

        // Build series: for each crop, sum desired_amount per harvest week
        const series = cropNames.map(cropName => {
            const data = weeks.map(week => {
                let total = 0;
                plantings.filter(p => p.crop_name === cropName).forEach(p => {
                    const hStart = getWeek(p.harvest_at);
                    const hEnd = getWeek(p.harvest_until) || hStart;
                    if (hStart && week >= hStart && week <= hEnd) {
                        total += (p.desired_amount || 0);
                    }
                });
                return total;
            });
            return {name: cropName, data};
        });

        return {
            categories: weeks.map(w => `${translate('week')} ${w}`),
            series,
        };
    }, [planResult, translate]);

    const planRowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    };

    const planErrors = planResult?.summary?.errors || [];
    const hasPlantings = planResult && planResult.proposed_plantings && planResult.proposed_plantings.length > 0;

    return (
        <div>
            <Row gutter={DEFAULT_GUTTER} align="middle">
                <Col md={12} xs={24}>
                    <h1>{translate('Auto Planner')}</h1>
                </Col>
                <Col md={12} xs={24} className="text-right">
                    <Select
                        value={year}
                        onChange={setYear}
                        options={yearOptions}
                        style={{width: 100, marginRight: 8}}
                    />
                    <Button
                        icon={<PlusOutlined/>}
                        type="primary"
                        onClick={openAddModal}
                        className="mar-r-1"
                    >
                        {translate('Doel toevoegen')}
                    </Button>
                    <Button
                        icon={<RocketOutlined/>}
                        disabled={goals.length === 0}
                        loading={planLoading}
                        onClick={handleGeneratePlan}
                    >
                        {translate('Plan genereren')}
                    </Button>
                </Col>
            </Row>

            <Table
                dataSource={goals}
                columns={allColumns}
                loading={loading}
                rowKey="id"
                pagination={false}
                locale={{emptyText: loading ? ' ' : translate('auto_planner.empty.no_goals')}}
            />

            {/* Plan loading spinner */}
            {planLoading && (
                <div className="text-center mar-t-2 mar-b-2">
                    <Spin size="large" tip={translate('auto_planner.loading.generating_plan')} />
                </div>
            )}

            {planResult && (
                <>
                    <Divider/>

                    {/* Errors from plan generation */}
                    {planErrors.length > 0 && (
                        <div className="mar-b-2">
                            {planErrors.map((err, i) => (
                                <Alert
                                    key={i}
                                    type="warning"
                                    showIcon
                                    className="mar-b-1"
                                    message={renderWarning(err, translate)}
                                />
                            ))}
                        </div>
                    )}

                    {/* Summary Stats */}
                    <Row gutter={DEFAULT_GUTTER} className="mar-b-2">
                        <Col xs={8}>
                            <Card size="small">
                                <Statistic
                                    title={translate('Totaal zaaiingen')}
                                    value={planResult.summary?.total_sowings || 0}
                                />
                            </Card>
                        </Col>
                        <Col xs={8}>
                            <Card size="small">
                                <Statistic
                                    title={translate('Waarschuwingen')}
                                    value={planResult.summary?.warnings_count || 0}
                                    valueStyle={planResult.summary?.warnings_count > 0 ? {color: '#faad14'} : undefined}
                                />
                            </Card>
                        </Col>
                        <Col xs={8}>
                            <Card size="small">
                                <Statistic
                                    title={translate('Oogstweken gedekt')}
                                    value={planResult.summary?.harvest_weeks_covered || 0}
                                    suffix={planResult.summary?.harvest_weeks_requested ? `/ ${planResult.summary.harvest_weeks_requested}` : ''}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Harvest Calendar Chart */}
                    {chartData && (
                        <Card title={translate('Oogstkalender')} className="mar-b-2" size="small">
                            <Chart
                                options={{
                                    chart: {
                                        id: 'harvest-calendar',
                                        type: 'bar',
                                        stacked: true,
                                        toolbar: {show: true},
                                    },
                                    xaxis: {
                                        categories: chartData.categories,
                                    },
                                    yaxis: {
                                        title: {text: translate('Hoeveelheid')},
                                        labels: {
                                            formatter: (val) => Math.floor(val),
                                        },
                                    },
                                    plotOptions: {
                                        bar: {
                                            horizontal: false,
                                            columnWidth: '70%',
                                        },
                                    },
                                    dataLabels: {enabled: false},
                                    legend: {position: 'top'},
                                    tooltip: {
                                        y: {
                                            formatter: (val) => `${val}`,
                                        },
                                    },
                                }}
                                series={chartData.series}
                                type="bar"
                                height={350}
                            />
                        </Card>
                    )}

                    {/* Empty state when plan has no plantings */}
                    {!hasPlantings && (
                        <Empty
                            description={translate('auto_planner.empty.no_plantings_generated')}
                            className="mar-t-2 mar-b-2"
                        />
                    )}

                    {/* Proposed Sowings Table */}
                    {hasPlantings && (
                        <Card
                            title={translate('Voorgestelde zaaiingen')}
                            size="small"
                            extra={
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<CheckOutlined/>}
                                        loading={addingToPlan}
                                        disabled={selectedRowKeys.length === 0}
                                        onClick={() => createPlantingsFromRows(selectedRowKeys)}
                                    >
                                        {translate('Geselecteerde toevoegen')} ({selectedRowKeys.length})
                                    </Button>
                                    <Button
                                        icon={<CheckOutlined/>}
                                        loading={addingToPlan}
                                        disabled={!planResult.proposed_plantings || planResult.proposed_plantings.length === 0}
                                        onClick={() => createPlantingsFromRows(planResult.proposed_plantings.map((_, i) => i))}
                                    >
                                        {translate('Alles toevoegen')}
                                    </Button>
                                </Space>
                            }
                        >
                            <Table
                                dataSource={planResult.proposed_plantings || []}
                                columns={planColumns}
                                rowKey={(_, index) => index}
                                rowSelection={planRowSelection}
                                pagination={false}
                                size="small"
                                scroll={{x: 900}}
                            />
                        </Card>
                    )}
                </>
            )}

            {/* Empty state when no goals */}
            {!loading && goals.length === 0 && !planResult && (
                <Empty
                    description={translate('auto_planner.empty.no_goals')}
                    className="mar-t-2"
                />
            )}

            <Modal
                title={editingGoal ? translate('Oogstdoel bewerken') : translate('Oogstdoel toevoegen')}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setEditingGoal(null);
                }}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="crop_id"
                        label={translate('Gewas')}
                        rules={[{required: true, message: translate('Selecteer een gewas')}]}
                    >
                        <Select
                            showSearch
                            placeholder={translate('Zoek een gewas...')}
                            filterOption={false}
                            onSearch={loadCrops}
                            loading={cropsLoading}
                            options={crops.map(c => ({
                                value: c.id,
                                label: (
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                        {c.image ? <img src={c.image} style={{width: 24, height: 24, borderRadius: 4, objectFit: 'cover'}} alt="" /> : <div style={{width: 24, height: 24, borderRadius: 4, background: '#e8f5e9'}} />}
                                        <span>{c.name}</span>
                                    </div>
                                ),
                            }))}
                            onChange={(cropId) => {
                                if (!editingGoal) {
                                    const crop = crops.find(c => c.id === cropId);
                                    if (crop && crop.harvest_min && crop.harvest_max) {
                                        const startWeek = MONTH_FIRST_WEEKS[crop.harvest_min - 1];
                                        const endWeek = MONTH_LAST_WEEKS[crop.harvest_max - 1];
                                        form.setFieldsValue({start_week: startWeek, end_week: endWeek});
                                    }
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="amount_per_week"
                        label={translate('Hoeveelheid per week')}
                        rules={[{required: true, message: translate('Vul een hoeveelheid in')}]}
                    >
                        <InputNumber min={1} style={{width: '100%'}}/>
                    </Form.Item>

                    <Row gutter={DEFAULT_GUTTER}>
                        <Col span={12}>
                            <Form.Item
                                name="start_week"
                                label={translate('Startweek')}
                                rules={[{required: true, message: translate('Vul een startweek in')}]}
                            >
                                <Select
                                    showSearch
                                    options={weekOptions}
                                    filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                                    placeholder={translate('Kies week')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="end_week"
                                label={translate('Eindweek')}
                                rules={[{required: true, message: translate('Vul een eindweek in')}]}
                            >
                                <Select
                                    showSearch
                                    options={weekOptions}
                                    filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                                    placeholder={translate('Kies week')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="priority"
                        label={translate('Prioriteit')}
                        rules={[{required: true}]}
                    >
                        <Select options={PRIORITIES.map(p => ({value: p, label: translate(p)}))}/>
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={submitting}>
                            {editingGoal ? translate('Opslaan') : translate('Toevoegen')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
